import notification from "antd/es/notification";
import deepmerge from "deepmerge";
import { put, select, takeEvery } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { AnyEntity, EntityTypesSupportingExport } from "@mapmycustomers/shared/types/entity";
import DistanceRecord from "@mapmycustomers/shared/types/entity/Distance";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";

import { getAllUsersByIdOfEntireOrganization } from "@app/store/members";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import { isCustomField } from "@app/util/fieldModel/impl/assert";
import CustomField from "@app/util/fieldModel/impl/CustomField";
import downloadEntitiesAsCsv from "@app/util/file/downloadEntitiesAsCsv";
import { formatDate } from "@app/util/formatters";
import loggingService from "@app/util/logging";
import { getEntityTypeDisplayName } from "@app/util/ui";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import i18nService from "../../config/I18nService";
import Iam from "../../types/Iam";
import Report from "../../types/Report";
import ApiService, { ApiMethodName } from "../api/ApiService";
import { callApi } from "../api/callApi";
import { handleError } from "../errors/actions";
import { getMe, getOrganizationId } from "../iam";

import { exportEntities } from "./actions";
import { MAX_ITEMS_TO_DOWNLOAD_FILE } from "./const";

const entityTypeToFetcher: Record<EntityTypesSupportingExport, ApiMethodName> = {
  [EntityType.ACTIVITY]: "fetchActivities",
  [EntityType.COMPANY]: "fetchCompanies",
  [EntityType.DEAL]: "fetchDeals",
  [EntityType.DISTANCE]: "fetchDistance",
  [EntityType.PERSON]: "fetchPeople",
  [EntityType.TRIP]: "fetchTrips",
};

export function* onExportEntities({
  payload: { entityType, exportCreationSuccessMessage, name, platformRequest, total, viewState },
}: ReturnType<typeof exportEntities.request>) {
  const $filters = {
    ...(viewState.filter && viewState.columns
      ? convertToPlatformFilterModel(
          viewState.filter,
          viewState.columns,
          getFieldModelByEntityType(entityType)
        )
      : {}),
    ...platformRequest?.$filters,
  };
  const orgId: Organization["id"] = yield select(getOrganizationId);

  if (total === undefined) {
    const requestPayload = {
      $filters,
      $limit: 0,
      ...(viewState?.sort ? { $order: convertToPlatformSortModel(viewState.sort) } : {}),
    };

    const response: ListResponse<AnyEntity> = yield callApi(
      entityTypeToFetcher[entityType] as keyof ApiService,
      orgId,
      requestPayload
    );
    total = response.total;
  }

  const intl = i18nService.getIntl();
  const exportName =
    name ??
    (intl
      ? getEntityTypeDisplayName(intl, entityType, {
          lowercase: false,
          plural: true,
        })
      : entityType);
  const visibleFields = viewState.columns?.filter(({ visible }) => visible) ?? [];
  const columns = new Set();
  visibleFields.forEach(({ field }) => {
    if (field.hasFeature(FieldFeature.DEAL_ADDRESS_FIELD)) {
      columns.add(DealFieldName.COMPANY);
      columns.add(DealFieldName.PERSON);
    } else if (field.hasFeature(FieldFeature.ACTIVITY_TYPE_FIELD)) {
      columns.add(ActivityFieldName.COMPANY);
      columns.add(ActivityFieldName.PERSON);
    }
  });

  try {
    if (total <= MAX_ITEMS_TO_DOWNLOAD_FILE) {
      const requestPayload = deepmerge(
        {
          $filters: {
            ...$filters,
            includeFiles: visibleFields.some(({ field }) =>
              field.hasFeature(FieldFeature.FILE_FIELD)
            ),
          },
          $limit: -1,
          ...(viewState.sort ? { $order: convertToPlatformSortModel(viewState.sort) } : {}),
        },
        platformRequest ?? {}
      );

      const response: ListResponse<AnyEntity> = yield callApi(
        entityTypeToFetcher[entityType],
        orgId,
        requestPayload
      );

      // Distance rows carry only userId, so the Name column would export blank. The drill-down
      // grid attaches the user for the same reason.
      let data = response.data;
      if (entityType === EntityType.DISTANCE) {
        const usersById: Map<User["id"], User> = yield select(getAllUsersByIdOfEntireOrganization);
        // a distance row is a metric row rather than an AnyEntity, hence the cast at this seam
        const rows = response.data as unknown as DistanceRecord[];
        data = rows.map((row) => ({
          ...row,
          user: usersById.get(row.userId),
        })) as unknown as AnyEntity[];
      }

      downloadEntitiesAsCsv(
        exportName,
        entityType,
        data,
        viewState.columns
          ? viewState.columns.filter(({ visible }) => visible).map(({ field }) => field)
          : undefined
      );
    } else {
      // Reports has no table for either, so the over-limit report path cannot serve them.
      if (entityType === EntityType.DISTANCE || entityType === EntityType.TRIP) {
        loggingService.warning(`Tried to download more than ${MAX_ITEMS_TO_DOWNLOAD_FILE} drives`, {
          viewState,
        });
        return;
      }

      const customFieldIds = (viewState.columns ?? [])
        .filter(({ field, visible }) => visible && isCustomField(field))
        .map(({ field }) => (field as CustomField).customFieldData.id);

      const columnNames = (viewState.columns ?? [])
        .filter(({ field, visible }) => visible && !isCustomField(field))
        .map(({ field }) => field.exportColumnName);

      const payload: Partial<Report> = {
        customFields: customFieldIds,
        description: "",
        name: `${exportName} ${formatDate(new Date(), "PPpp")}`,
        selectedColumns: columnNames,
        selectedFilters: $filters,
        tableName: entityType as Exclude<
          EntityTypesSupportingExport,
          EntityType.DISTANCE | EntityType.TRIP
        >,
      };
      const iam: Iam = yield select(getMe);

      const report: Report = yield callApi("createReport", orgId, payload);
      yield callApi("generateReport", orgId, report.id, iam.username);
      if (exportCreationSuccessMessage) {
        notification.success({
          message: exportCreationSuccessMessage,
        });
      }
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* exportEntitiesSaga() {
  yield takeEvery(exportEntities.request, onExportEntities);
}
